import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import BettingContainer from './components/BettingContainer';
import AdminDashboard from './components/AdminDashboard';
import Wallet from './components/Wallet';
import BackgroundMusic from './components/BackgroundMusic';
import { HowItWorksModal, ReferralGateModal, TransactionHistoryModal } from './components/Modal';
import Modal from './components/Modal/Modal';
import { AirdropHeaderWidget } from './components/AirdropWidget/AirdropHeaderWidget';
import { AirdropModalContent } from './components/AirdropWidget/AirdropModal';
// Simplified contexts
import { WalletProvider, useWallet } from './contexts/WalletProvider';
import { GameSessionProvider } from './contexts/GameSessionProvider';
import { AirdropProvider } from './contexts/AirdropContext';
import { ReferralProvider, useReferral } from './contexts/ReferralContext';
import TransactionAnimationProvider from './contexts/TransactionAnimationContext';
import PrivyProviderWrapper from './contexts/PrivyProvider';
// import { useProfileSetup } from './contexts/ProfileSetupContext';
import { useAuth } from './hooks/useAuth';
import { useGameSession } from './contexts/GameSessionProvider';
import { Analytics } from '@vercel/analytics/react';
import './App.css';
import { VoucherProvider } from './contexts/VoucherContext';

// Main App Layout - handles header and routing
const AppLayout: React.FC = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname === '/admin';
  
  // Only need backend health from auth hook
  const { backendHealthy, handleLogout } = useAuth();
  
  // Get game session loading state (must be called unconditionally)
  // GameSessionProvider internally waits for wallet to be ready before checking session
  const gameSession = useGameSession();
  
  // Referral gate check
  const { hasAccess, needsReferral, pendingCode } = useReferral();
  const { address: wallet } = useWallet();
  
  // Profile setup context
  // const { showProfileSetup, setShowProfileSetup } = useProfileSetup();
  
  // How It Works modal state
  const [showHowItWorks, setShowHowItWorks] = useState(false);
  
  // Airdrop modal state
  const [showAirdropModal, setShowAirdropModal] = useState(false);
  
  // Past Games modal state
  const [showPastGamesModal, setShowPastGamesModal] = useState(false);

  // Simple viewport height management for mobile
  useEffect(() => {
    const updateViewportHeight = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    updateViewportHeight();
    window.addEventListener('resize', updateViewportHeight);
    window.addEventListener('orientationchange', updateViewportHeight);
    
    return () => {
      window.removeEventListener('resize', updateViewportHeight);
      window.removeEventListener('orientationchange', updateViewportHeight);
    };
  }, []);

  // Show backend connection error
  if (backendHealthy === false) {
    return (
      <div className="app">
        <div className="connection-error">
          <h2>🔌 Connection Error</h2>
          <p>Unable to connect to the FEEN backend server.</p>
          <p>Please check your internet connection and try again.</p>
          <button 
            onClick={() => window.location.reload()} 
            className="retry-button"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  // Show loading while checking backend health OR initial game session
  // Don't render the app until:
  // 1. Backend is confirmed healthy
  // 2. Session check is complete (GameSessionProvider waits for wallet ready internally)
  // 3. Initial WebSocket data is ready (race matchup available)
  if (backendHealthy === null || (!isAdminRoute && gameSession && (!gameSession.sessionCheckComplete || !gameSession.raceMatchup))) {
    return (
      <div className="app">
        <div className="connection-loading">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  // Check if we should show referral gate
  // Show gate if:
  // 1. No code in URL AND no wallet (prompt to enter code or connect)
  // 2. No code in URL AND wallet connected AND no access
  const shouldShowReferralGate = 
    !isAdminRoute && 
    !pendingCode && 
    (!wallet || (wallet && hasAccess === false && needsReferral));

  return (
    <div className="app">
      {/* Referral Gate - blocks access if needed */}
      {shouldShowReferralGate && <ReferralGateModal />}
      
      {/* Only show header for non-admin routes */}
      {!isAdminRoute && (
        <header className="app-header">
          {/* Left side - Logo and brand */}
          <div className="header-left">
            <div className="brand-logo">
              <img 
                src="/assets/icons/feen-header.svg" 
                alt="FEEN" 
                className="brand-logo-svg"
                onError={(e) => {
                  console.error('Logo failed to load, retrying...', e);
                  e.currentTarget.src = "/assets/icons/feen-header.svg?v=" + Date.now();
                }}
              />
            </div>
          </div>

          {/* Center - How It Works */}
          {/* <div className="header-center">
            <button 
              className="how-it-works-button"
              onClick={() => setShowHowItWorks(true)}
            >
              How It Works
            </button>
          </div> */}

          {/* Airdrop Widget - Between center and right */}
          <AirdropHeaderWidget onClick={() => setShowAirdropModal(true)} />

          {/* Right side - Actions */}
          <div className="header-right">
            {/* Background Music Control */}
            <BackgroundMusic />
            
            {/* Support button - Telegram link */}
            <button 
              className="header-action-button"
              onClick={() => window.open('https://t.me/+YJOY7WL515k3NTZh', '_blank')}
              title="Get support on Telegram"
            >
              <img 
                src="/assets/icons/telegram-seeklogo.png" 
                alt="Telegram Support" 
                style={{ width: '20px', height: '20px' }}
              />
            </button>
            
            {/* Wallet - handles its own auth with overlay */}
            <div className="header-wallet">
              <Wallet 
                onLogout={handleLogout}
                onPastGamesClick={() => setShowPastGamesModal(true)}
              />
            </div>
          </div>
        </header>
      )}

      <main className={`app-main ${isAdminRoute ? 'admin-main' : ''}`}>
        {/* Route-based content */}
        <Routes>
          <Route path="/" element={<BettingContainer />} />
          <Route path="/admin" element={<AdminDashboard />} />
          {/* Catch-all route for referral codes - treats /CODE as home page with referral */}
          <Route path="/:referralCode" element={<BettingContainer />} />
        </Routes>
      </main>

      {/* Profile Setup Modal */}
      {/* {showProfileSetup && (
        <ProfileSetup
          onComplete={() => setShowProfileSetup(false)}
          onSkip={() => setShowProfileSetup(false)}
        />
      )} */}

      {/* How It Works Modal */}
      <HowItWorksModal 
        isOpen={showHowItWorks}
        onClose={() => setShowHowItWorks(false)}
      />

      {/* Airdrop Modal */}
      <Modal 
        isOpen={showAirdropModal}
        onClose={() => setShowAirdropModal(false)}
        title="Live Airdrop"
        className="airdrop-modal"
      >
        <AirdropModalContent />
      </Modal>

      {/* Past Games Modal */}
      {wallet && (
        <TransactionHistoryModal
          isOpen={showPastGamesModal}
          onClose={() => setShowPastGamesModal(false)}
          userWallet={wallet}
        />
      )}

      {/* Vercel Analytics - disabled in development for clean testing */}
      {process.env.NODE_ENV === 'production' && <Analytics />}
    </div>
  );
};

// Main App component that wraps everything with contexts
const App: React.FC = () => {
  return (
    <Router>
      <PrivyProviderWrapper>
        <WalletProvider>
          <ReferralProvider>
            <VoucherProvider>
              {/* <ProfileSetupProvider> */}
                <GameSessionProvider>
                  <TransactionAnimationProvider>
                    <AirdropProvider>
                      <AppLayout />
                    </AirdropProvider>
                  </TransactionAnimationProvider>
                </GameSessionProvider>
              {/* </ProfileSetupProvider> */}
            </VoucherProvider>
          </ReferralProvider>
        </WalletProvider>
      </PrivyProviderWrapper>
    </Router>
  );
};

export default App;